import { useEffect, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  setDoc,
} from "firebase/firestore";
import { db } from "../../../firebase/config";
import { Athlete } from "../../../types";

export default function AthleteTable() {
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [currentlyEditing, setCurrentlyEditing] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<Athlete>>({});
  const [filterGender, setFilterGender] = useState<string>("");
  const [filterMinAge, setFilterMinAge] = useState<number | "">("");
  const [filterMaxAge, setFilterMaxAge] = useState<number | "">("");

  useEffect(() => {
    const fetchAthletes = async () => {
      const snapshot = await getDocs(collection(db, "athletes"));
      const data = snapshot.docs.map((doc) => doc.data() as Athlete);
      setAthletes(data);
    };

    fetchAthletes();
  }, []);

  const handleDelete = async (email: string) => {
    await deleteDoc(doc(db, "athletes", email));
    setAthletes((prev) => prev.filter((a) => a.email !== email));
  };

  const handleEdit = (athlete: Athlete) => {
    setCurrentlyEditing(athlete.email);
    setEditForm(athlete);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    if (!editForm.email) return;
    await setDoc(doc(db, "athletes", editForm.email), editForm);
    setAthletes((prev) =>
      prev.map((a) => (a.email === editForm.email ? (editForm as Athlete) : a))
    );
    setCurrentlyEditing(null);
    setEditForm({});
  };

  const handleCancel = () => {
    setCurrentlyEditing(null);
    setEditForm({});
  };

  const calculateAge = (birthdayStr: string): number => {
    const today = new Date();
    const birthDate = new Date(birthdayStr);
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <>
      <div className="admin-table-filters">
        <div className="admin-filter-group">
          <label className="admin-filter-label">
            Gender:
            <select
              className="admin-filter-input"
              value={filterGender}
              onChange={(e) => setFilterGender(e.target.value)}
            >
              <option value="">All</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </label>
          <label className="admin-filter-label">
            Min Age:
            <input
              className="admin-filter-input"
              type="number"
              value={filterMinAge}
              onChange={(e) =>
                setFilterMinAge(e.target.value ? parseInt(e.target.value) : "")
              }
            />
          </label>
          <label className="admin-filter-label">
            Max Age:
            <input
              className="admin-filter-input"
              type="number"
              value={filterMaxAge}
              onChange={(e) =>
                setFilterMaxAge(e.target.value ? parseInt(e.target.value) : "")
              }
            />
          </label>
        </div>
      </div>
      <table className="admin-data-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Birthday</th>
            <th>Gender</th>
            <th>Email</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {athletes
            .filter((athlete) => {
              const age = calculateAge(athlete.birthday);
              if (filterGender && athlete.gender !== filterGender) return false;
              if (filterMinAge !== "" && age < filterMinAge) return false;
              if (filterMaxAge !== "" && age > filterMaxAge) return false;
              return true;
            })
            .map((athlete) => {
              const isEditing = currentlyEditing === athlete.email;
              return (
                <tr key={athlete.email}>
                  <td>
                    {isEditing ? (
                      <>
                        <input
                          name="firstName"
                          value={editForm.firstName || ""}
                          onChange={handleChange}
                          className="admin-edit-input"
                        />
                        <input
                          name="lastName"
                          value={editForm.lastName || ""}
                          onChange={handleChange}
                          className="admin-edit-input"
                        />
                      </>
                    ) : (
                      `${athlete.firstName} ${athlete.lastName}`
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <input
                        name="birthday"
                        type="date"
                        value={editForm.birthday || ""}
                        onChange={handleChange}
                        className="admin-edit-input"
                      />
                    ) : (
                      athlete.birthday
                    )}
                  </td>
                  <td>
                    {isEditing ? (
                      <select
                        name="gender"
                        value={editForm.gender || ""}
                        onChange={handleChange}
                        className="admin-edit-input"
                      >
                        <option value="">Select Gender</option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Other</option>
                      </select>
                    ) : (
                      athlete.gender
                    )}
                  </td>
                  <td>{athlete.email}</td>
                  <td>
                    {isEditing ? (
                      <>
                        <button onClick={handleSave}>Save</button>
                        <button onClick={handleCancel}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => handleEdit(athlete)}>
                          Edit
                        </button>
                        <button onClick={() => handleDelete(athlete.email)}>
                          Delete
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>
    </>
  );
}
